/**
 * Public, unauthenticated live delivery tracking page.
 *
 * URL: `https://order.tappya.com/track/{token}` (see `PUBLIC_TRACKING_BASE_URL`
 * on the backend — `order.tappya.com` is this app's own path-based host per
 * `resolveSlug.ts`'s `PATH_BASED_DOMAINS`, and `/track/:token` is registered
 * OUTSIDE the tenant `basename`/`BranchLayout`, exactly like `pin/:token`:
 * a driver-location link must work with no tenant slug and no signed-in
 * customer). Custom tenant domains (`burgerinboxeg.com/...`) do NOT need to
 * serve this route — the token alone identifies the delivery, so every
 * tracking link should point at `order.tappya.com`, never a tenant domain.
 *
 * No customer PII is requested or shown: the backend response carries only
 * `{status, statusLabel, branchName, driverFirstName, vehicleType,
 * driverLocation, etaMinutes, updatedAt}` — nothing that identifies the
 * customer, so a leaked link is low-value, same design intent as
 * PinLocationScreen.
 *
 * Polling: every 15s while the trip is active, stopped once the status is
 * terminal (delivered/cancelled/failed — see `isTerminalStatus`). A failed
 * poll (network blip, 5xx) does NOT stop polling — it backs off (30s, 60s,
 * capped) and keeps the last-known state on screen rather than flashing an
 * error every 15s. A 404 is different: the token itself is invalid/expired,
 * which polling harder can never fix, so it goes straight to the terminal
 * "link expired" state.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import {
  trackingApi,
  TrackingNotFoundError,
  type PublicTrackingResponse,
} from "@/api/tracking";
import {
  TIMELINE_STEPS,
  isStepReached,
  isTerminalStatus,
  isCancelledStatus,
  type TimelineStep,
} from "@/lib/deliveryTrackingStatus";

const POLL_INTERVAL_MS = 15_000;
const BACKOFF_STEPS_MS = [30_000, 60_000, 60_000];

type PageState = "loading" | "ready" | "expired" | "error";

const STEP_ICON: Record<TimelineStep, keyof typeof Icons> = {
  assigned: "checkCircle",
  at_store: "store",
  picked_up: "bag",
  on_the_way: "bike",
  at_customer: "home",
  delivered: "pin",
};

export function PublicDeliveryTrackingScreen() {
  const { token = "" } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  const [pageState, setPageState] = useState<PageState>("loading");
  const [data, setData] = useState<PublicTrackingResponse | null>(null);
  const failureCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!token) {
      setPageState("expired");
      return;
    }

    async function poll() {
      try {
        const res = await trackingApi.get(token);
        if (cancelledRef.current) return;
        failureCount.current = 0;
        setData(res);
        setPageState("ready");
        if (isTerminalStatus(res.status)) {
          return; // Stop polling — the trip is over.
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelledRef.current) return;
        if (err instanceof TrackingNotFoundError) {
          setPageState("expired");
          return; // Never retry an expired/unknown token.
        }
        // Transient failure: keep whatever we last rendered, back off, and
        // only show a hard error state if we've never loaded anything yet.
        setPageState((prev) => (prev === "ready" ? prev : "error"));
        const backoff =
          BACKOFF_STEPS_MS[Math.min(failureCount.current, BACKOFF_STEPS_MS.length - 1)];
        failureCount.current += 1;
        timerRef.current = setTimeout(poll, backoff);
      }
    }

    void poll();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [token]);

  const stepLabels: Record<TimelineStep, string> = useMemo(
    () => ({
      assigned: t("v2.tracking.step_assigned", "Driver assigned"),
      at_store: t("v2.tracking.step_at_store", "Rider at the restaurant"),
      picked_up: t("v2.tracking.step_picked_up", "Picked up"),
      on_the_way: t("v2.tracking.step_on_the_way", "On the way"),
      at_customer: t("v2.tracking.step_at_customer", "Your rider has arrived"),
      delivered: t("v2.tracking.step_delivered", "Delivered"),
    }),
    [t],
  );

  if (pageState === "loading") {
    return (
      <div style={outerStyle} dir={isRtl ? "rtl" : "ltr"}>
        <div style={cardStyle}>
          <p style={bodyText}>{t("v2.tracking.loading", "Loading your delivery…")}</p>
        </div>
      </div>
    );
  }

  if (pageState === "expired") {
    return (
      <div style={outerStyle} dir={isRtl ? "rtl" : "ltr"}>
        <div style={cardStyle}>
          <Icons.pin size={28} style={{ color: "var(--ink-3)" }} />
          <p style={titleText}>{t("v2.tracking.expired_title", "This link is no longer valid")}</p>
          <p style={bodyText}>
            {t(
              "v2.tracking.expired_body",
              "It may have expired or the delivery is no longer active.",
            )}
          </p>
        </div>
      </div>
    );
  }

  if (pageState === "error" && !data) {
    return (
      <div style={outerStyle} dir={isRtl ? "rtl" : "ltr"}>
        <div style={cardStyle}>
          <p style={bodyText}>
            {t("v2.tracking.load_error", "Couldn't load your delivery status. Retrying…")}
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const cancelled = isCancelledStatus(data.status);
  const label = i18n.language === "ar" ? data.statusLabel.ar : data.statusLabel.en;

  return (
    <div style={pageShellStyle} dir={isRtl ? "rtl" : "ltr"}>
      <div style={headerStyle}>
        {data.branchName && <p style={eyebrowText}>{data.branchName}</p>}
        <p style={statusText} data-testid="tracking-status-label">
          {label}
        </p>
        {data.etaMinutes && !cancelled && (
          <p style={etaText}>
            {t("v2.tracking.eta", {
              min: data.etaMinutes[0],
              max: data.etaMinutes[1],
              defaultValue: "{{min}}–{{max}} min",
            })}
          </p>
        )}
      </div>

      {!cancelled && (
        <ol style={timelineStyle} aria-label={t("v2.tracking.timeline_label", "Delivery progress")}>
          {TIMELINE_STEPS.map((step) => {
            const reached = isStepReached(data.status, step);
            const Icon = Icons[STEP_ICON[step]];
            return (
              <li key={step} style={timelineItemStyle}>
                <div
                  style={{
                    ...timelineDotStyle,
                    background: reached ? "var(--primary-600)" : "var(--surface-2)",
                    color: reached ? "#fff" : "var(--ink-3)",
                  }}
                >
                  <Icon size={16} />
                </div>
                <span style={{ ...timelineLabelStyle, color: reached ? "var(--ink-1)" : "var(--ink-3)" }}>
                  {stepLabels[step]}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {(data.driverFirstName || data.vehicleType) && (
        <div style={driverCardStyle}>
          <Icons.bike size={20} style={{ color: "var(--primary-600)" }} />
          <div>
            {data.driverFirstName && (
              <p style={driverNameText}>{data.driverFirstName}</p>
            )}
            {data.vehicleType && <p style={bodyText}>{data.vehicleType}</p>}
          </div>
        </div>
      )}

      {/* Map only when a live driver position is known — no new map SDK, no
          static fallback pretending to be live. See global constraint. */}
      {data.driverLocation ? (
        <DriverMapCard location={data.driverLocation} />
      ) : (
        !cancelled && (
          <div style={staticCardStyle}>
            <Icons.truck size={22} style={{ color: "var(--ink-3)" }} />
            <p style={bodyText}>
              {t("v2.tracking.no_location", "Your order is on the way — live location isn't available yet.")}
            </p>
          </div>
        )
      )}

      {cancelled && (
        <div style={staticCardStyle}>
          <p style={bodyText}>{t("v2.tracking.cancelled", "This delivery was cancelled.")}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Reuses the app's existing map stack (maplibre-gl, already a dependency and
 * already used by `PinLocationScreen`/`MapPickerScreen`) rather than adding
 * a new SDK. Lazily constructed so a page that never has a driver location
 * yet never pays for a map instance.
 */
function DriverMapCard({ location }: { location: { lat: number; lng: number } }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<import("maplibre-gl").Map | null>(null);
  const markerRef = useRef<import("maplibre-gl").Marker | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;
    async function mount() {
      const [{ default: maplibregl }] = await Promise.all([
        import("maplibre-gl"),
        import("maplibre-gl/dist/maplibre-gl.css"),
      ]);
      if (cancelled || !containerRef.current) return;
      const { DEFAULT_MAP_STYLE } = await import("@/lib/location");
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: DEFAULT_MAP_STYLE,
        center: [location.lng, location.lat],
        zoom: 15,
        interactive: true,
      });
      const marker = new maplibregl.Marker({ color: "#1E88E5" })
        .setLngLat([location.lng, location.lat])
        .addTo(map);
      mapRef.current = map;
      markerRef.current = marker;
    }
    void mount();
    return () => {
      cancelled = true;
      markerRef.current?.remove();
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep an already-mounted map's marker following updates without
  // rebuilding the whole map instance every 15s poll.
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setLngLat([location.lng, location.lat]);
      mapRef.current?.panTo([location.lng, location.lat]);
    }
  }, [location.lat, location.lng]);

  return (
    <div style={mapCardStyle}>
      <div
        data-testid="tracking-map"
        ref={containerRef}
        style={{ position: "absolute", inset: 0, borderRadius: "inherit" }}
        aria-label={t("v2.tracking.map_label", "Driver location")}
      />
    </div>
  );
}

const outerStyle: CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  background: "var(--surface-2)",
};

const cardStyle: CSSProperties = {
  maxWidth: 360,
  width: "100%",
  textAlign: "center",
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-card)",
  padding: 24,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
};

const pageShellStyle: CSSProperties = {
  minHeight: "100vh",
  background: "var(--surface-2)",
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 14,
  maxWidth: 480,
  margin: "0 auto",
};

const headerStyle: CSSProperties = {
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-card)",
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const eyebrowText: CSSProperties = {
  fontSize: 11,
  color: "var(--ink-3)",
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: "uppercase",
};

const statusText: CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: "var(--ink-1)",
  margin: 0,
};

const etaText: CSSProperties = {
  fontSize: 13,
  color: "var(--primary-600)",
  fontWeight: 600,
  margin: 0,
};

const timelineStyle: CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 16,
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-card)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const timelineItemStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const timelineDotStyle: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 14,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};

const timelineLabelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
};

const driverCardStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-card)",
  padding: 14,
};

const driverNameText: CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: "var(--ink-1)",
  margin: 0,
};

const staticCardStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-card)",
  padding: 16,
};

const mapCardStyle: CSSProperties = {
  position: "relative",
  height: 220,
  borderRadius: "var(--radius-card)",
  overflow: "hidden",
  border: "1px solid var(--border)",
  background: "var(--surface-2)",
};

const bodyText: CSSProperties = {
  fontSize: 13,
  color: "var(--ink-2)",
  margin: 0,
};

const titleText: CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: "var(--ink-1)",
  margin: 0,
};
